import {
  nextTick,
  onBeforeUpdate,
  onMounted,
  onUpdated,
  ref,
  watch,
} from 'vue';
import { useResizeObserver } from '@vueuse/core';

export const useInternalChatHistoryScroll = (getGeneration = () => 0) => {
  const viewport = ref(null);
  const content = ref(null);
  let anchor;
  let atEnd = true;
  let managedScrollTop;
  let paging;

  const nearEnd = () => {
    const container = viewport.value;
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <= 2
    );
  };

  const capture = () => {
    const container = viewport.value;
    if (!container) return null;
    const top = container.getBoundingClientRect().top;
    const visible = [...container.querySelectorAll('[data-message-id]')].find(
      element => element.getBoundingClientRect().bottom > top
    );
    return (
      visible && {
        id: visible.dataset.messageId,
        offset: visible.getBoundingClientRect().top - top,
      }
    );
  };
  const restore = () => {
    const container = viewport.value;
    if (!container) return;
    if (atEnd && (!paging || !anchor)) {
      container.scrollTop = container.scrollHeight;
      anchor = capture();
    } else if (anchor) {
      const element = container.querySelector(
        `[data-message-id="${anchor.id}"]`
      );
      if (element) {
        container.scrollTop +=
          element.getBoundingClientRect().top -
          container.getBoundingClientRect().top -
          anchor.offset;
      } else {
        // Showing the latest replies can replace the window in this generation.
        anchor = capture();
        atEnd = nearEnd();
      }
    }
    managedScrollTop = container.scrollTop;
  };
  const onScroll = () => {
    const container = viewport.value;
    if (!container || container.scrollTop === managedScrollTop) return;
    atEnd = nearEnd();
    anchor = capture();
    managedScrollTop = container.scrollTop;
  };
  watch(
    getGeneration,
    () => {
      anchor = null;
      atEnd = true;
      paging = null;
      managedScrollTop = viewport.value?.scrollTop;
    },
    { flush: 'sync' }
  );
  onMounted(restore);
  // A scroll event may still be queued when loading/error state starts a render.
  onBeforeUpdate(onScroll);
  onUpdated(restore);
  useResizeObserver([viewport, content], () => {
    restore();
    if (viewport.value && !paging) atEnd = nearEnd();
  });

  const preserve = async action => {
    onScroll();
    // Paging is an explicit reading operation, including at a loaded window's end.
    const container = viewport.value;
    const owner = { generation: getGeneration(), container };
    paging = owner;
    if (atEnd || !anchor) anchor = capture();
    const focused = document.activeElement;
    try {
      await action();
    } finally {
      await nextTick();
      if (
        paging === owner &&
        owner.generation === getGeneration() &&
        container === viewport.value
      ) {
        // Keep the current bookmark: the reader can scroll while a page loads.
        onScroll();
        restore();
        const focusAnchor = anchor || capture();
        if (
          focused &&
          !focused.isConnected &&
          document.activeElement === document.body
        ) {
          container
            ?.querySelector(`[data-message-id="${focusAnchor?.id}"]`)
            ?.focus({ preventScroll: true });
        }
        paging = null;
        atEnd = nearEnd();
      }
    }
  };
  return { viewport, content, onScroll, preserve };
};
